'use client';

import { useEffect, useState } from 'react';

/* UTILS */
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { fr } from 'date-fns/locale';
import { ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/* COMPONENTS */
import { Calendar } from '@/components/ui/Calendar/Calender';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover/Popover';
import { InputForm } from '../ui/Input/InputForm';

interface DatePickerProps {
  defaultValue?: Date;
  label?: string;
  placeholder?: string;
  handleChange: (date: Date | undefined) => void;
  error?: string;
  required?: boolean;
}

export function DatePicker({
  defaultValue,
  label,
  handleChange,
  placeholder,
  error,
  required = false,
}: DatePickerProps) {
  const { i18n } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const normalizedLanguage = i18n.resolvedLanguage?.toLowerCase() ?? i18n.language?.toLowerCase() ?? 'en';
  const locale = normalizedLanguage.startsWith('fr')
    ? fr
    : normalizedLanguage.startsWith('ja')
      ? ja
      : enUS;

  useEffect(() => {
    if (defaultValue && !date) {
      setDate(defaultValue);
    }
  }, [defaultValue, date])

  const onDateChange = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    handleChange(selectedDate);
    setIsPopoverOpen(false);
  };

  return (
    <div className={'flex flex-col !h-[52px] w-full'}>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <div>
            <InputForm
              label={label}
              labelClassName="text-bodyLarge leading-none !pb-3 !pt-0 group-disabled:cursor-not-allowed group-disabled:opacity-70"
              value={date ? format(date, 'dd/MM/yyyy', { locale }) : ''}
              placeholder={placeholder}
              required={required}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            locale={locale}
            mode="single"
            selected={date}
            onSelect={onDateChange}
            initialFocus
            toDate={new Date()}
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-red-500 text-bodyMedium">{error}</p>}
    </div >
  );
}
